import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Crown, CreditCard, Sparkles, Zap, ShieldCheck, Headphones, Globe, Target, Cpu, XCircle } from "lucide-react";
import DashboardLayout from "../dashboard/DashboardLayout";
import { trackPageView, trackButtonClick, trackFeatureClick } from "../../utils/analytics";
import axiosInstance from "../../utils/axiosInstance";

const INR = new Intl.NumberFormat("en-IN");

// Icon mapping for plans
const iconMap = {
  starter: Zap,
  pro: Crown,
  elite: Target,
};

const PricingCard = ({ plan, onStartTrial, currentTier }) => {
  const Icon = iconMap[plan.id] || Zap;
  const isCurrentPlan = currentTier === plan.id;

  return (
    <div
      className={`relative rounded-[2rem] md:rounded-[3rem] border-2 bg-white p-6 md:p-10 shadow-sm transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10 group overflow-hidden ${
        plan.highlight ? "border-blue-600 shadow-xl shadow-blue-500/5 ring-4 ring-blue-50" : "border-slate-100 hover:border-blue-200"
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-1 -right-1">
           <div className="bg-blue-600 text-white text-[9px] font-black uppercase tracking-[0.2em] px-6 py-2 rounded-bl-3xl shadow-lg italic">
              Most Popular
           </div>
        </div>
      )}

      {isCurrentPlan && (
        <div className="absolute top-4 left-4">
           <div className="bg-emerald-500 text-white text-[9px] font-black uppercase tracking-[0.15em] px-4 py-1.5 rounded-full shadow-lg italic">
              Current Plan
           </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-10">
        <div className="text-xl font-black text-slate-900 tracking-tight italic">{plan.name}</div>
        <div
          className={`flex h-14 w-14 items-center justify-center rounded-[1.5rem] transition-all duration-500 shadow-inner ${
            plan.highlight ? "bg-blue-600 text-white scale-110" : "bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500"
          }`}
        >
          <Icon size={24} />
        </div>
      </div>

      <div className="mb-10 flex items-baseline gap-2">
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">INR</span>
        <span className="text-5xl font-black text-slate-900 tracking-tighter italic">{INR.format(plan.priceInr)}</span>
        <span className="text-xs font-black text-slate-400 uppercase tracking-widest italic">/ month</span>
      </div>

      <div className="space-y-5 mb-12">
        {(plan.features || []).map((feature) => (
          <div key={feature} className="flex items-start gap-4">
            <div className={`mt-0.5 p-1 rounded-lg ${plan.highlight ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
               <CheckCircle2 size={14} className="stroke-[3]" />
            </div>
            <span className="text-xs font-bold text-slate-600 leading-tight italic">{feature}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onStartTrial(plan.id)}
        disabled={isCurrentPlan}
        className={`w-full py-5 rounded-[1.5rem] text-[10px] font-black uppercase tracking-[0.2em] transition-all active:scale-95 italic shadow-2xl ${
          isCurrentPlan
            ? "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none"
            : plan.highlight
            ? "bg-blue-600 text-white hover:bg-black shadow-blue-500/20"
            : "bg-slate-900 text-white hover:bg-black shadow-slate-200"
        }`}
      >
        {isCurrentPlan ? "Current Plan" : "Start Free Trial"}
      </button>

      <div className="mt-6 flex items-center justify-center gap-2 opacity-50">
         <ShieldCheck size={14} className="text-emerald-500" />
         <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.1em]">7-Day Money Back Guarantee</span>
      </div>
    </div>
  );
};

const CallbackModal = ({ isOpen, onClose, onSubmit, planName, userData, loading, success }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [phone, setPhone] = useState(userData?.phone || "");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
        <div className="bg-slate-900 px-6 py-6 sm:px-8 sm:py-8 text-white relative shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-slate-400 hover:text-white transition-colors">
            <XCircle size={20} />
          </button>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Headphones size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-black italic tracking-tight">Request Call Back</h3>
              <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest leading-none mt-1">For {planName} Plan</p>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto p-6 sm:p-8">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 text-center animate-in zoom-in-95 duration-500">
               <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-inner border border-emerald-100">
                  <ShieldCheck size={40} className="stroke-[2.5]" />
               </div>
               <h3 className="text-2xl font-black text-slate-900 italic tracking-tight mb-2">Request Received!</h3>
               <p className="text-slate-500 font-bold text-xs uppercase tracking-tight max-w-[250px]">
                  Our team will contact you on <span className="text-blue-600">{date}</span> at <span className="text-blue-600">{time}</span>.
               </p>
               <button 
                onClick={onClose}
                className="mt-8 px-10 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all active:scale-95 shadow-xl shadow-slate-200"
               >
                  Back to Pricing
               </button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-5">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic ml-1">First Name</label>
                  <input 
                    type="text" 
                    disabled 
                    value={userData?.first_name || ""} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-slate-500 font-bold italic text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic ml-1">Last Name</label>
                  <input 
                    type="text" 
                    disabled 
                    value={userData?.last_name || ""} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-slate-500 font-bold italic text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic ml-1">Email Address</label>
                <input 
                  type="email" 
                  disabled 
                  value={userData?.email || ""} 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-slate-500 font-bold italic text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic ml-1">Phone Number</label>
                <input 
                  type="tel" 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 font-bold italic text-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic ml-1">Preferred Date</label>
                  <input 
                    type="date" 
                    value={date} 
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 font-bold italic text-xs focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic ml-1">Preferred Time</label>
                  <input 
                    type="time" 
                    value={time} 
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 font-bold italic text-xs focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all outline-none"
                  />
                </div>
              </div>

              <button
                onClick={() => onSubmit({ date, time, phone })}
                disabled={!date || !time || !phone || loading}
                className={`w-full py-4 sm:py-5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all italic shadow-lg flex items-center justify-center gap-3 ${
                  !date || !time || !phone || loading
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-black shadow-blue-500/20 active:scale-95"
                }`}
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles size={14} />}
                Confirm Request
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Pricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlanForLead, setSelectedPlanForLead] = useState(null);
  const [leadLoading, setLeadLoading] = useState(false);
  const [isLeadSuccess, setIsLeadSuccess] = useState(false);
  
  useEffect(() => {
    trackPageView("/pricing", "Pricing Plans");
    
    // Fetch pricing from backend
    const fetchPricing = async () => {
      try {
        const response = await axiosInstance.get("/pricing");
        const tiers = response.data?.tiers || [];
        setPlans(tiers);
      } catch (err) {
        console.error("Failed to fetch pricing:", err);
        setPlans([
          { id: "starter", name: "Starter", priceInr: 999, features: ["Limited features"] },
          { id: "pro", name: "Professional", priceInr: 1999, highlight: true, features: ["AI Match", "2Agents"] },
          { id: "elite", name: "Enterprise", priceInr: 4999, features: ["Everything", "5 agents"] },
        ]);
      }
    };
    
    // Fetch current subscription status
    const fetchSubscription = async () => {
      try {
        const response = await axiosInstance.get("/subscription/status");
        setCurrentTier(response.data?.tier?.id || null);
      } catch {
        setCurrentTier(null);
      }
    };

    // Fetch user profile for pre-filling callback form
    const fetchUserProfile = async () => {
      try {
        const response = await axiosInstance.get("/user");
        setUserData(response.data);
      } catch (err) {
        console.error("Failed to fetch user profile:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPricing();
    fetchSubscription();
    fetchUserProfile();
  }, []);

  const handleStartTrial = (planId) => {
    if (!planId) return;
    const plan = plans.find(p => p.id === planId);
    setSelectedPlanForLead(plan);
    setIsModalOpen(true);
    
    trackButtonClick(`Open Callback Modal - ${plan?.name}`, "Pricing");
  };

  const handleLeadSubmit = async (leadData) => {
    setLeadLoading(true);
    try {
      await axiosInstance.post("/pricing/leads", {
        plan_id: selectedPlanForLead?.id,
        callback_date: leadData.date,
        callback_time: leadData.time,
        phone: leadData.phone,
        first_name: userData?.first_name,
        last_name: userData?.last_name,
        email: userData?.email
      });
      
      trackFeatureClick(`Callback Requested: ${selectedPlanForLead?.name}`);
      setIsLeadSuccess(true);
    } catch (err) {
      console.error("Failed to submit lead:", err);
      // We can still use a proper notification if we had one, but for now we'll just log
      // and maybe show an error state if needed, but let's stick to the prompt's focus on success.
    } finally {
      setLeadLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setIsLeadSuccess(false), 300); // Reset after closing animation
  };

  const handleTabChange = (tabId) => navigate("/dashboard", { state: { tab: tabId } });

  return (
    <DashboardLayout activeTab="pricing" setTab={handleTabChange}>
      <div className="mx-auto w-full max-w-7xl space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20 font-sans">
        
        <div className="bg-slate-900 border border-slate-800 rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 text-center lg:text-left flex flex-col lg:flex-row items-center justify-between gap-10 overflow-hidden relative shadow-2xl shadow-blue-900/10">
          <div className="absolute top-0 right-0 p-8 md:p-12 opacity-10 pointer-events-none -rotate-12 group-hover:rotate-0 transition-transform duration-1000">
             <Cpu size={150} className="text-blue-500 md:w-[200px] md:h-[200px]" />
          </div>
          
          <div className="relative z-10 w-full max-w-2xl">
            <h1 className="text-3xl md:text-5xl font-black text-white tracking-tighter italic">Choose Your Plan</h1>
            <p className="text-slate-400 mt-4 text-sm font-medium italic leading-relaxed uppercase tracking-tight">
               Scale your job search capabilities. Upgrade to unlock advanced automation, multiple agents, and premium features to dominate your job search.
            </p>
          </div>
          <div className="relative z-10 flex gap-4">
             <div className="px-6 py-4 bg-white/5 border border-white/10 rounded-[2rem] text-center">
                <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1 italic">Support</div>
                <div className="text-2xl font-black text-white italic">24/7</div>
             </div>
             <div className="px-6 py-4 bg-white/5 border border-white/10 rounded-[2rem] text-center">
                <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1 italic">Success Rate</div>
                <div className="text-2xl font-black text-white italic">99.8%</div>
             </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {plans.map((plan) => (
            <PricingCard key={plan.id} plan={plan} onStartTrial={handleStartTrial} currentTier={currentTier} />
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-[4rem] overflow-hidden shadow-sm">
          <div className="p-5 md:p-10 border-b border-slate-100 flex flex-col md:flex-row items-center justify-between bg-slate-50/50 gap-6">
             <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3 italic">
                   <Zap size={24} className="text-blue-600 fill-blue-600" /> Feature Comparison
                </h2>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1 italic">Detailed breakdown of all features</p>
             </div>
             <div className="flex items-center gap-4 bg-white px-6 py-3 rounded-2xl shadow-inner border border-slate-100">
                <span className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-500 tracking-widest"><ShieldCheck size={16} className="text-emerald-500"/> Secure</span>
                <div className="w-px h-4 bg-slate-200" />
                <span className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-500 tracking-widest"><Globe size={16} className="text-blue-500"/> Global</span>
             </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-50/20">
                  <th className="px-4 md:px-10 py-6 text-left text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] italic">Feature</th>
                  <th className="px-4 md:px-10 py-6 text-left text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] italic">Starter</th>
                  <th className="px-4 md:px-10 py-6 text-left text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] italic">Professional</th>
                  <th className="px-4 md:px-10 py-6 text-left text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] italic">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {[
                  ["Smart Profile & CV Builder", "Included", "Included", "Included"],
                  ["Job Match Tracking", "Unlimited", "Unlimited", "Unlimited"],
                  ["Form Auto-fill", "Basic", "Advanced", "Premium"],
                  ["Automated Job Agents", "None", "2 Agents", "5 Agents"],
                  ["HR Contact Search", "100 Searches", "500 Searches", "Unlimited"],
                  ["Direct HR Emails", "100 Emails", "500 Emails", "Unlimited"],
                  ["Job Targeting", "Standard", "Priority", "Advanced"],
                  ["Customer Support", "Email", "Priority", "Dedicated"],
                ].map((row, idx) => (
                  <tr key={idx} className="hover:bg-blue-50/30 transition-colors group">
                    <td className="px-4 md:px-10 py-6 text-[11px] font-black text-slate-700 italic uppercase tracking-tight">{row[0]}</td>
                    <td className="px-4 md:px-10 py-6 text-xs font-bold text-slate-500 italic uppercase">{row[1]}</td>
                    <td className="px-4 md:px-10 py-6 text-[11px] font-black text-blue-600 italic uppercase bg-blue-50/20 border-x border-blue-50 font-sans tracking-wide">{row[2]}</td>
                    <td className="px-4 md:px-10 py-6 text-xs font-bold text-slate-500 italic uppercase">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CallbackModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleLeadSubmit}
        planName={selectedPlanForLead?.name}
        userData={userData}
        loading={leadLoading}
        success={isLeadSuccess}
      />
    </DashboardLayout>
  );
};

export default Pricing;
